import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/constants';
import BottomNav from '../components/BottomNav';

const AuthPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  // Separate state for create account form in rating flow
  const [createEmail, setCreateEmail] = useState('');
  const [createUsername, setCreateUsername] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [showCreatePassword, setShowCreatePassword] = useState(true);
  const [createPasswordError, setCreatePasswordError] = useState('');
  const [createUsernameError, setCreateUsernameError] = useState('');
  const [createAgreedToTerms, setCreateAgreedToTerms] = useState(false);
  const [isCreateSubmitting, setIsCreateSubmitting] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for reset_token in URL on page load
  useEffect(() => {
    const tokenFromUrl = searchParams.get('reset_token');
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setShowResetPassword(true);
      setShowForgotPassword(false);
    }
  }, [searchParams]);

  // Check if coming from rating attempt
  const isRatingFlow = searchParams.get('reason') === 'rate';
  const reason = searchParams.get('reason');
  // Always show the compact auth flow form (with both sign in and create account)
  const isAuthFlow = true;

  // Get the appropriate header message based on reason
  const getAuthHeaderMessage = () => {
    switch (reason) {
      case 'rate':
        return <>Sign in or Create an Account<br />to rank cigars!</>;
      case 'notifications':
        return <>Sign in or Create an Account<br />to view Notifications!</>;
      case 'favorites':
        return <>Sign in or Create an Account<br />to see your favorites!</>;
      case 'discover':
        return <>Sign in or Create an Account<br />to discover new cigars!</>;
      case 'forum':
        return <>Sign in or Create an Account<br />to view the Forum!</>;
      case 'leaderboards':
        return <>Sign in or Create an Account<br />to view the Leaderboards!</>;
      case 'profile':
        return <>Sign in or Create an Account<br />to view your Profile!</>;
      default:
        return <>Sign in or Create an Account</>;
    }
  };

  // Scroll to top when rating flow page opens
  useEffect(() => {
    if (isAuthFlow) {
      window.scrollTo(0, 0);
    }
  }, [isAuthFlow]);

  // Password validation
  const validatePassword = (pwd) => {
    if (pwd.length < 5) {
      return 'Password must be at least 5 characters';
    }
    return '';
  };

  // Username validation
  const validateUsername = (uname) => {
    if (uname.length < 3) {
      return 'Username must be at least 3 characters';
    }
    if (!/^[a-zA-Z0-9_]+$/.test(uname)) {
      return 'Username can only contain letters, numbers, and underscores';
    }
    return '';
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    if (!isLogin && value) {
      setPasswordError(validatePassword(value));
    } else {
      setPasswordError('');
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    setUsername(value);
    if (value) {
      setUsernameError(validateUsername(value));
    } else {
      setUsernameError('');
    }
  };

  // Handlers for create account form in rating flow
  const handleCreatePasswordChange = (e) => {
    const value = e.target.value;
    setCreatePassword(value);
    if (value) {
      setCreatePasswordError(validatePassword(value));
    } else {
      setCreatePasswordError('');
    }
  };

  const handleCreateUsernameChange = (e) => {
    const value = e.target.value;
    setCreateUsername(value);
    if (value) {
      setCreateUsernameError(validateUsername(value));
    } else {
      setCreateUsernameError('');
    }
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    
    const pwdError = validatePassword(createPassword);
    const unameError = validateUsername(createUsername);
    
    if (pwdError) {
      setCreatePasswordError(pwdError);
      toast.error(pwdError);
      return;
    }
    if (unameError) {
      setCreateUsernameError(unameError);
      toast.error(unameError);
      return;
    }
    
    setIsCreateSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/register`, { 
        email: createEmail, 
        username: createUsername, 
        password: createPassword 
      });
      toast.success('Account created successfully! Please sign in above.');
      // Clear form fields
      setCreateEmail('');
      setCreatePassword('');
      setCreateUsername('');
      setCreateAgreedToTerms(false);
    } catch (error) {
      let errorMessage = error.response?.data?.detail || error.message || 'An error occurred';
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage.map(e => e.msg || e.message).join(', ');
      } else if (typeof errorMessage === 'object') {
        errorMessage = JSON.stringify(errorMessage);
      }
      toast.error(errorMessage);
    } finally {
      setIsCreateSubmitting(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Password reset instructions sent!');
      
      // For demo purposes, we show the reset token (remove in production)
      if (res.data.reset_token) {
        setResetToken(res.data.reset_token);
        setShowForgotPassword(false);
        setShowResetPassword(true);
      }
    } catch (error) {
      toast.error('Failed to process request');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    const pwdError = validatePassword(newPassword);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully! Please sign in.');
      setShowResetPassword(false);
      setIsLogin(true);
      setNewPassword('');
      setConfirmPassword('');
      setResetToken('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate before submit (for registration)
    if (!isLogin) {
      const pwdError = validatePassword(password);
      const unameError = validateUsername(username);
      
      if (pwdError) {
        setPasswordError(pwdError);
        toast.error(pwdError);
        return;
      }
      if (unameError) {
        setUsernameError(unameError);
        toast.error(unameError);
        return;
      }
    }
    
    setIsSubmitting(true);
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin ? { email, password } : { email, username, password };
      const res = await axios.post(`${API}${endpoint}`, payload);
      
      if (isLogin) {
        // Login: store token and redirect to success page
        login(res.data.token, res.data.user);
        navigate('/login-success');
      } else {
        // Registration: show success message and redirect to login page
        toast.success('Account created successfully! Please sign in.');
        setIsLogin(true);
        // Clear form fields
        setPassword('');
        setUsername('');
      }
    } catch (error) {
      let errorMessage = error.response?.data?.detail || error.message || 'An error occurred';
      
      // Handle validation errors from backend
      if (Array.isArray(errorMessage)) {
        errorMessage = errorMessage.map(e => e.msg || e.message).join(', ');
      } else if (typeof errorMessage === 'object') {
        errorMessage = JSON.stringify(errorMessage);
      }
      
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0B0B0C] p-4 pb-20">
      <div className="w-full max-w-md">
        <div className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl ${isAuthFlow ? 'p-4' : 'p-8'}`}>
          
          {/* Forgot Password View */}
          {showForgotPassword ? (
            <>
              <button 
                onClick={() => setShowForgotPassword(false)}
                className="flex items-center text-white/70 hover:text-white mb-4"
              >
                <ArrowLeft size={20} className="mr-2" />
                Back to Sign In
              </button>
              <h1 className="playfair text-3xl text-white text-center mb-4">
                Forgot Password
              </h1>
              <p className="text-white/60 text-sm text-center mb-6">
                Enter your email address and we&apos;ll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white font-semibold rounded-full py-6"
                >
                  {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            </>
          ) : showResetPassword ? (
            /* Reset Password View */
            <>
              <h1 className="playfair text-3xl text-white text-center mb-4">
                Reset Password
              </h1>
              <p className="text-white/60 text-sm text-center mb-6">
                Enter your new password below.
              </p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="New password (min 5 characters)"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                />
                {newPassword && confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-red-400 text-xs flex items-center gap-1">
                    <AlertCircle size={12} />
                    Passwords do not match
                  </p>
                )}
                {newPassword && confirmPassword && newPassword === confirmPassword && newPassword.length >= 5 && (
                  <p className="text-green-400 text-xs flex items-center gap-1">
                    <CheckCircle size={12} />
                    Passwords match
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={isSubmitting || newPassword !== confirmPassword || newPassword.length < 5}
                  className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white font-semibold rounded-full py-6 disabled:opacity-50"
                >
                  {isSubmitting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
              <button
                onClick={() => {
                  setShowResetPassword(false);
                  setShowForgotPassword(true);
                }}
                className="w-full mt-4 text-white/60 hover:text-white text-sm"
              >
                Request a new reset link
              </button>
            </>
          ) : isAuthFlow ? (
            /* Auth Flow - Show both Sign In and Create Account */
            <>
              {getAuthHeaderMessage() && (
                <h1 className="playfair text-xl text-amber-500 text-center mb-2">
                  {getAuthHeaderMessage()}
                </h1>
              )}
              
              {/* Sign In Box */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3 mb-2">
                <h2 className="text-base font-semibold text-white mb-2">Sign In</h2>
                <form onSubmit={handleSubmit} className="space-y-2">
                  <Input
                    data-testid="email"
                    type="text"
                    placeholder="Email or Username"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9 text-sm"
                  />
                  <div className="relative">
                    <Input
                      data-testid="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-10 h-9 text-sm"
                      style={{
                        WebkitTextFillColor: 'white',
                        transition: 'background-color 5000s ease-in-out 0s'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10 bg-transparent pointer-events-auto"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <Button
                    data-testid="login-submit"
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white font-semibold rounded-full py-2 h-9 text-sm transition-all disabled:opacity-50"
                  >
                    {isSubmitting ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>
                <button
                  onClick={() => setShowForgotPassword(true)}
                  className="w-full mt-1 text-white/60 hover:text-white text-xs"
                >
                  Forgot Password?
                </button>
              </div>

              {/* Create Account Box */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                <h2 className="text-base font-semibold text-white mb-2">Create Account</h2>
                <form onSubmit={handleCreateSubmit} className="space-y-2">
                  <Input
                    type="email"
                    placeholder="Email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9 text-sm"
                  />
                  <Input
                    type="text"
                    placeholder="Username (min 3 char)"
                    value={createUsername}
                    onChange={handleCreateUsernameChange}
                    required
                    className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 h-9 text-sm ${
                      createUsernameError ? 'border-red-500' : ''
                    }`}
                  />
                  <div className="relative">
                    <Input
                      type={showCreatePassword ? "text" : "password"}
                      placeholder="Password (min 5 char)"
                      value={createPassword}
                      onChange={handleCreatePasswordChange}
                      required
                      className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-10 h-9 text-sm ${
                        createPasswordError ? 'border-red-500' : ''
                      }`}
                      style={{
                        WebkitTextFillColor: 'white',
                        transition: 'background-color 5000s ease-in-out 0s'
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCreatePassword(!showCreatePassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10 bg-transparent pointer-events-auto"
                    >
                      {showCreatePassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      id="create-agree-terms"
                      checked={createAgreedToTerms}
                      onChange={(e) => setCreateAgreedToTerms(e.target.checked)}
                      className="mt-0.5 w-3.5 h-3.5 rounded border-white/30 bg-white/5 text-amber-600 focus:ring-amber-500"
                    />
                    <label htmlFor="create-agree-terms" className="text-white/70 text-xs leading-tight">
                      I agree to the{' '}
                      <Link 
                        to="/terms-of-service" 
                        className="text-amber-500 hover:text-amber-400 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Terms of Service
                      </Link>
                      {' '}and{' '}
                      <Link 
                        to="/privacy-policy" 
                        className="text-amber-500 hover:text-amber-400 underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Privacy Policy
                      </Link>
                    </label>
                  </div>
                  <Button
                    type="submit"
                    disabled={isCreateSubmitting || createPasswordError || createUsernameError || !createAgreedToTerms}
                    className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white font-semibold rounded-full py-2 h-9 text-sm transition-all disabled:opacity-50"
                  >
                    {isCreateSubmitting ? 'Creating...' : 'Create Account'}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            /* Normal Login/Register View */
            <>
              <h1 className="playfair text-4xl text-white text-center mb-8">
                {isLogin ? 'Sign In' : 'Create Account'}
              </h1>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Input
                    data-testid="email"
                    type={isLogin ? "text" : "email"}
                    placeholder={isLogin ? "Email or Username" : "Email"}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                  />
                </div>
            
                {!isLogin && (
              <div>
                <Input
                  data-testid="username"
                  type="text"
                  placeholder="Username (min 3 characters)"
                  value={username}
                  onChange={handleUsernameChange}
                  required
                  className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 ${
                    usernameError ? 'border-red-500' : ''
                  }`}
                />
                {usernameError && (
                  <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle size={12} />
                    {usernameError}
                  </p>
                )}
              </div>
            )}
            
            <div>
              <div className="relative">
                <Input
                  data-testid="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={isLogin ? "Password" : "Password (min 5 characters)"}
                  value={password}
                  onChange={handlePasswordChange}
                  required
                  className={`bg-white/5 border-white/10 text-white placeholder:text-white/30 pr-12 ${
                    passwordError ? 'border-red-500' : ''
                  }`}
                  style={{
                    WebkitTextFillColor: 'white',
                    transition: 'background-color 5000s ease-in-out 0s'
                  }}
                />
                <button
                  data-testid="toggle-password-visibility"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/70 hover:text-white transition-colors z-10 bg-transparent pointer-events-auto"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {!isLogin && passwordError && (
                <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle size={12} />
                  {passwordError}
                </p>
              )}
              {!isLogin && password.length >= 5 && !passwordError && (
                <p className="text-green-400 text-xs mt-1 flex items-center gap-1">
                  <CheckCircle size={12} />
                  Password meets requirements
                </p>
              )}
            </div>
            
            {/* Terms and Privacy Policy checkbox - only show for Create Account */}
            {!isLogin && (
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="mt-1 w-4 h-4 rounded border-white/30 bg-white/5 text-amber-600 focus:ring-amber-500"
                />
                <label htmlFor="agree-terms" className="text-white/70 text-sm">
                  I agree to the{' '}
                  <Link 
                    to="/terms-of-service" 
                    className="text-amber-500 hover:text-amber-400 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </Link>
                  {' '}and{' '}
                  <Link 
                    to="/privacy-policy" 
                    className="text-amber-500 hover:text-amber-400 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Privacy Policy
                  </Link>
                </label>
              </div>
            )}
            
            <Button
              data-testid="login-submit"
              type="submit"
              disabled={isSubmitting || (!isLogin && (passwordError || usernameError || !agreedToTerms))}
              className="w-full bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white font-semibold rounded-full py-6 transition-all disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </Button>
          </form>
          
          {isLogin && (
            <button
              onClick={() => setShowForgotPassword(true)}
              className="w-full mt-3 text-white/60 hover:text-white text-sm"
            >
              Forgot Password?
            </button>
          )}
          
          <button
            data-testid="auth-toggle-button"
            onClick={() => {
              const switchingToCreateAccount = isLogin;
              setIsLogin(!isLogin);
              setPasswordError('');
              setUsernameError('');
              setShowPassword(switchingToCreateAccount);
            }}
            className="w-full mt-4 text-amber-500 hover:text-amber-400 text-sm"
          >
            {isLogin ? "Don't have an account? Create one" : 'Already have an account? Sign in'}
          </button>
            </>
          )}
        </div>
      </div>
      <BottomNav />
    </div>
  );
};

export default AuthPage;
